import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, from, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { GenerateMfaCodeResponse } from "src/app/modules/index/multifactor-auth/multifactor-auth.types";
import { environment } from "../../../environments/environment";
import { RegisterUser } from "../../modules/index/register/register.type";
import { Company } from "../../modules/portal/companies/company.type";
import { Partner } from "../../modules/portal/partners/partner.type";
import { ResetPassword, User } from "../../modules/portal/users/user.type";
import { UserTokens } from "../../shared/types/app.type";
import { UserInfo, UserRight, UserType } from "../../shared/types/user-info";
import { QueryParams } from "../http/query-params.type";
import { LocalStorageKeys } from "../storage/local-storage";

interface UserSession {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  type: UserType;
  right: UserRight;
  isCWRep: boolean;
  isSalesRep: boolean;
  companyId: string;
  company: Company;
  partnerId: string;
  partner: Partner;
  email: string;
  selectedCompanyId: string;
  selectedCompany: Company;
  companies: Company[];
  lastLoginAt: Date;
}

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  currentUser: Observable<UserInfo>;
  private currentUserSubject: BehaviorSubject<UserInfo>;

  constructor(private http: HttpClient) {
    this.currentUserSubject = new BehaviorSubject<UserInfo>(
      JSON.parse(localStorage.getItem(LocalStorageKeys.currentUser)),
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  get currentUserInfo(): UserInfo {
    const currentUserFromLocalStorage = JSON.parse(localStorage.getItem(LocalStorageKeys.currentUser));

    if (currentUserFromLocalStorage) {
      return new UserInfo(currentUserFromLocalStorage);
    }

    return null;
  }

  login(email: string, password: string): Observable<UserTokens> {
    return this.http.post<UserTokens>(`${environment.serverUrl}/auth/login`, {
      email,
      password,
    });
  }

  logout(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.currentUserSubject.next(null);
  }

  registerUser(body: RegisterUser): Observable<User> {
    return this.http.post<User>(`${environment.serverUrl}/auth/register`, body);
  }

  refreshToken(refreshToken: string): Observable<void> {
    const userTokens$ = this.http.post<UserTokens>(`${environment.serverUrl}/auth/token/refresh`, {
      refreshToken,
    });

    return this.setSession(userTokens$);
  }

  revokeRefreshToken(refreshToken: string): Observable<void> {
    return this.http.post<void>(`${environment.serverUrl}/auth/token/revoke`, {
      token: refreshToken,
    });
  }

  requestForgotPassword(email: string): Observable<unknown> {
    return this.http.post(`${environment.serverUrl}/users/forgot-password`, { email });
  }

  resetPassword(body: ResetPassword): Observable<unknown> {
    return this.http.post(`${environment.serverUrl}/users/reset-password`, body);
  }

  confirmAccount(code: string, options?: QueryParams): Observable<unknown> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get(`${environment.serverUrl}/users/confirm?code=${code}`, { params }).pipe(
      map((response: Response) => response),
      catchError(this.handleError<unknown>("")),
    );
  }

  setSession(userTokens$: Observable<UserTokens>, reloadPage?: boolean): Observable<void> {
    return from(userTokens$).pipe(
      switchMap((tokens) =>
        this.getCurrentUser(tokens.accessToken).pipe(
          map((user) => {
            const userSession = this.getUserSession(user, tokens);
            localStorage.setItem(LocalStorageKeys.currentUser, JSON.stringify(userSession));
            if (reloadPage) {
              window.location.reload();
            }
          }),
        ),
      ),
    );
  }

  generateMfaCode(mfaToken: string): Observable<GenerateMfaCodeResponse> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = { Authorization: `bearer ${mfaToken}` };
    return this.http.post<GenerateMfaCodeResponse>(`${environment.serverUrl}/auth/mfa/generate-code`, null, {
      headers,
    });
  }

  verifyMfaCode(mfaToken: string, code: string): Observable<UserTokens> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = { Authorization: `bearer ${mfaToken}` };
    const body = {
      code,
    };
    return this.http.post(`${environment.serverUrl}/auth/mfa/verify-code`, body, { headers });
  }

  /** Get the current user data through the http response */
  private getCurrentUser(token?: string): Observable<User> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = token ? { Authorization: `bearer ${token}` } : undefined;
    return this.http.get<User>(`${environment.serverUrl}/users/current`, { headers });
  }

  /** Get the user session object by merging the user data and tokens */
  private getUserSession(user: User, tokens: UserTokens): UserSession {
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      type: user.type,
      right: user.right,
      isCWRep: user.isCWRep,
      isSalesRep: user.isSalesRep,
      companyId: user.companyId,
      company: user.company,
      partnerId: user.partnerId,
      partner: user.partner,
      email: user.email,
      selectedCompanyId: user.selectedCompanyId,
      selectedCompany: user.selectedCompany,
      companies: user.companies,
      lastLoginAt: new Date(user.lastLoginAt),
      ...tokens,
    };
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
